/* Forme theme — core interactions (vanilla JS, no build step required) */
(() => {
  'use strict';

  const routes = window.themeRoutes || { cartUrl: '/cart', addUrl: '/cart/add', changeUrl: '/cart/change', cartJsonUrl: '/cart.js', predictiveSearchUrl: '/search/suggest' };

  /* ---------------- helpers ---------------- */
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 200) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };
  const fetchConfig = (method = 'GET', body) => {
    const cfg = { method, headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
    if (body) cfg.body = JSON.stringify(body);
    return cfg;
  };
  const trapFocus = (container) => {
    const focusable = qsa('a,button,input,select,textarea,[tabindex]:not([tabindex="-1"])', container).filter(el => !el.disabled);
    if (!focusable.length) return () => {};
    const first = focusable[0], last = focusable[focusable.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    first.focus();
    return () => container.removeEventListener('keydown', handler);
  };

  /* ---------------- backdrop (shared by drawers/modals) ---------------- */
  const backdrop = qs('[data-viewport-overlay]');
  let activeCloser = null;
  const lockScroll = (lock) => { document.documentElement.style.overflow = lock ? 'hidden' : ''; };
  const showBackdrop = (onClick) => {
    if (!backdrop) return;
    backdrop.setAttribute('open', '');
    lockScroll(true);
    backdrop.onclick = onClick;
  };
  const hideBackdrop = () => {
    if (!backdrop) return;
    backdrop.removeAttribute('open');
    lockScroll(false);
    backdrop.onclick = null;
  };
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && activeCloser) activeCloser(); });

  /* ---------------- Header ---------------- */
  class SiteHeader {
    constructor(el) {
      this.el = el;
      this.sticky = el.dataset.sticky === 'true';
      this.lastY = window.scrollY;
      this.bindDropdowns();
      this.bindMobileNav();
      if (this.sticky) this.bindSticky();
    }
    bindSticky() {
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this.el.classList.toggle('is-sticky', window.scrollY > 40);
          ticking = false;
        });
      }, { passive: true });
    }
    bindDropdowns() {
      qsa('.nav-item', this.el).forEach((item) => {
        const link = qs(':scope > .nav-link', item);
        if (!link) return;
        link.addEventListener('click', (e) => {
          const dropdown = qs('.nav-dropdown, .nav-mega', item);
          if (!dropdown) return;
          if (window.matchMedia('(hover: none)').matches) {
            e.preventDefault();
            const isOpen = item.classList.contains('is-open');
            qsa('.nav-item.is-open', this.el).forEach((i) => i.classList.remove('is-open'));
            item.classList.toggle('is-open', !isOpen);
          }
        });
      });
      document.addEventListener('click', (e) => {
        if (!this.el.contains(e.target)) qsa('.nav-item.is-open', this.el).forEach((i) => i.classList.remove('is-open'));
      });
    }
    bindMobileNav() {
      const toggle = qs('[data-mobile-nav-toggle]', this.el);
      const nav = qs('#MobileNav');
      if (!toggle || !nav) return;
      const close = () => { nav.removeAttribute('open'); hideBackdrop(); activeCloser = null; toggle.setAttribute('aria-expanded', 'false'); };
      toggle.addEventListener('click', () => {
        nav.setAttribute('open', '');
        toggle.setAttribute('aria-expanded', 'true');
        showBackdrop(close);
        activeCloser = close;
      });
      qs('[data-mobile-nav-close]', nav)?.addEventListener('click', close);
      qsa('[data-submenu-toggle]', nav).forEach((btn) => {
        btn.addEventListener('click', () => btn.closest('.mobile-nav__item').classList.toggle('is-open'));
      });
    }
  }

  /* ---------------- Cart ---------------- */
  class CartDrawer {
    constructor() {
      this.el = qs('#CartDrawer');
      this.toggles = qsa('[data-cart-toggle]');
      this.close = this.close.bind(this);
      this.toggles.forEach((t) => t.addEventListener('click', (e) => { e.preventDefault(); this.open(); }));
      qsa('[data-drawer-close]', this.el).forEach((b) => b.addEventListener('click', this.close));
      if (this.el) this.bindLineEvents();
    }
    open() {
      if (!this.el) return;
      this.el.setAttribute('open', '');
      showBackdrop(this.close);
      activeCloser = this.close;
      this.releaseFocus = trapFocus(this.el);
    }
    close() {
      if (!this.el) return;
      this.el.removeAttribute('open');
      hideBackdrop();
      activeCloser = null;
      this.releaseFocus && this.releaseFocus();
    }
    async refresh() {
      const res = await fetch(`${routes.cartUrl}?section_id=cart-drawer`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('#CartDrawer');
      if (fresh && this.el) {
        this.el.innerHTML = fresh.innerHTML;
        this.bindLineEvents();
        qsa('[data-drawer-close]', this.el).forEach((b) => b.addEventListener('click', this.close));
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }
    bindLineEvents() {
      qsa('[data-qty-decrease]', this.el).forEach((btn) => btn.addEventListener('click', () => this.stepQty(btn, -1)));
      qsa('[data-qty-increase]', this.el).forEach((btn) => btn.addEventListener('click', () => this.stepQty(btn, 1)));
      qsa('[data-qty-input]', this.el).forEach((input) => input.addEventListener('change', () => this.setQty(input.dataset.line, input.value)));
      qsa('[data-cart-remove]', this.el).forEach((btn) => btn.addEventListener('click', () => this.setQty(btn.dataset.line, 0)));
    }
    stepQty(btn, delta) {
      const input = qs(`[data-qty-input][data-line="${btn.dataset.line}"]`, this.el);
      if (!input) return;
      const next = Math.max(0, parseInt(input.value, 10) + delta);
      input.value = next;
      this.setQty(btn.dataset.line, next);
    }
    async setQty(line, quantity) {
      await fetch(routes.changeUrl, fetchConfig('POST', { line: Number(line), quantity: Number(quantity) }));
      this.refresh();
    }
  }

  async function addToCart(formData, { openDrawer = true } = {}) {
    const res = await fetch(routes.addUrl + '.js', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }
    if (window.forme?.cartDrawer) {
      await window.forme.cartDrawer.refresh();
      if (openDrawer) window.forme.cartDrawer.open();
    }
    return res.json();
  }

  /* ---------------- Product form (variant selection + add to cart) ---------------- */
  class ProductForm {
    constructor(root) {
      this.root = root;
      this.form = qs('form.product-form', root);
      if (!this.form) return;
      this.jsonData = JSON.parse(qs('[data-product-json]', root)?.textContent || '{}');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      qsa('[data-variant-input]', root).forEach((input) => input.addEventListener('change', () => this.onVariantChange()));
      qs('[data-qty-stepper]', root) && this.bindQtyStepper();
      this.onVariantChange(true);
    }
    bindQtyStepper() {
      const wrap = qs('[data-qty-stepper]', this.root);
      const input = qs('input', wrap);
      qs('[data-qty-decrease]', wrap)?.addEventListener('click', () => { input.value = Math.max(1, parseInt(input.value, 10) - 1); });
      qs('[data-qty-increase]', wrap)?.addEventListener('click', () => { input.value = parseInt(input.value, 10) + 1; });
    }
    currentSelection() {
      const options = qsa('[data-variant-input]:checked, select[data-variant-input]', this.root).map((el) => el.value);
      return options;
    }
    findVariant(options) {
      return (this.jsonData.variants || []).find((v) => v.options.every((o, i) => o === options[i]));
    }
    onVariantChange(initial = false) {
      const options = this.currentSelection();
      const variant = this.findVariant(options);
      const submit = qs('[data-add-to-cart]', this.root);
      const priceWrap = qs('[data-product-price]', this.root);
      if (variant) {
        if (!initial) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
        qs('[data-variant-id]', this.form).value = variant.id;
        if (priceWrap) priceWrap.innerHTML = this.renderPrice(variant);
        if (submit) {
          if (variant.available) { submit.disabled = false; submit.querySelector('[data-add-to-cart-text]').textContent = submit.dataset.addText; }
          else { submit.disabled = true; submit.querySelector('[data-add-to-cart-text]').textContent = submit.dataset.soldOutText; }
        }
        if (variant.featured_media) this.swapMedia(variant.featured_media.id);
      } else if (submit) {
        submit.disabled = true;
        submit.querySelector('[data-add-to-cart-text]').textContent = submit.dataset.unavailableText;
      }
      qsa('[data-variant-input]', this.root).forEach((input) => {
        if (input.tagName !== 'INPUT') return;
        const testOptions = [...options];
        testOptions[Number(input.dataset.optionIndex)] = input.value;
        const match = this.findVariant(testOptions);
        input.closest('.variant-pill, .variant-swatch')?.classList.toggle('is-unavailable', !!match && !match.available);
      });
    }
    renderPrice(variant) {
      const money = (cents) => window.forme.formatMoney(cents);
      if (variant.compare_at_price > variant.price) {
        return `<span class="price__sale">${money(variant.price)}</span><span class="price__compare">${money(variant.compare_at_price)}</span>`;
      }
      return `<span>${money(variant.price)}</span>`;
    }
    swapMedia(mediaId) {
      const target = document.querySelector(`[data-gallery-item][data-media-id="${mediaId}"]`);
      if (target) {
        target.click();
        target.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
      }
    }
    async onSubmit(e) {
      e.preventDefault();
      const submit = qs('[data-add-to-cart]', this.root);
      if (submit.disabled) return;
      submit.classList.add('button--loading');
      try {
        await addToCart(new FormData(this.form));
      } catch (err) {
        console.error(err);
      } finally {
        submit.classList.remove('button--loading');
      }
    }
  }

  /* ---------------- Quick add modal ---------------- */
  class QuickAddModal {
    constructor() {
      this.modal = qs('#QuickAddModal');
      if (!this.modal) return;
      this.panel = qs('[data-modal-content]', this.modal);
      this.close = this.close.bind(this);
      qs('[data-modal-close]', this.modal)?.addEventListener('click', this.close);
      qs('.modal__backdrop', this.modal)?.addEventListener('click', this.close);
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quick-add]');
        if (trigger) { e.preventDefault(); this.load(trigger.dataset.productUrl); }
      });
    }
    async load(url) {
      this.panel.innerHTML = '<div class="modal__loading"><div class="spinner"></div></div>';
      this.open();
      const res = await fetch(`${url}?section_id=quick-add`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.panel.innerHTML = doc.querySelector('[data-quick-add-content]')?.innerHTML || 'Unable to load product';
      new ProductForm(this.panel);
    }
    open() { this.modal.setAttribute('open', ''); showBackdrop(this.close); activeCloser = this.close; }
    close() { this.modal.removeAttribute('open'); hideBackdrop(); activeCloser = null; }
  }

  /* ---------------- Search drawer / predictive search ---------------- */
  class SearchDrawer {
    constructor() {
      this.el = qs('#SearchDrawer');
      if (!this.el) return;
      this.input = qs('[data-search-input]', this.el);
      this.results = qs('[data-search-results]', this.el);
      this.close = this.close.bind(this);
      qsa('[data-search-toggle]').forEach((t) => t.addEventListener('click', (e) => { e.preventDefault(); this.open(); }));
      qs('[data-drawer-close]', this.el)?.addEventListener('click', this.close);
      this.input?.addEventListener('input', debounce(() => this.search(this.input.value), 250));
    }
    open() {
      this.el.setAttribute('open', '');
      showBackdrop(this.close);
      activeCloser = this.close;
      setTimeout(() => this.input?.focus(), 150);
    }
    close() { this.el.removeAttribute('open'); hideBackdrop(); activeCloser = null; }
    async search(term) {
      if (!term || term.length < 2) { this.results.innerHTML = ''; return; }
      const res = await fetch(`${routes.predictiveSearchUrl}?q=${encodeURIComponent(term)}&section_id=predictive-search`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.results.innerHTML = doc.querySelector('[data-predictive-results]')?.innerHTML || '';
    }
  }

  /* ---------------- Accordion (FAQ + product tabs) ---------------- */
  function initAccordions() {
    qsa('[data-accordion]').forEach((item) => {
      const trigger = qs('[data-accordion-trigger]', item);
      const panel = qs('[data-accordion-panel]', item);
      if (!trigger || !panel) return;
      trigger.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');
        if (item.dataset.exclusive) {
          qsa('[data-accordion].is-open', item.closest('[data-accordion-group]') || document.body).forEach((sib) => {
            if (sib !== item) { sib.classList.remove('is-open'); qs('[data-accordion-panel]', sib).style.height = '0px'; }
          });
        }
        item.classList.toggle('is-open', !isOpen);
        panel.style.height = !isOpen ? panel.scrollHeight + 'px' : '0px';
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }

  /* ---------------- Announcement bar ---------------- */
  function initAnnouncementBar() {
    const bar = qs('[data-announcement-bar]');
    if (!bar) return;
    const track = qs('.announcement-bar__track', bar);
    const messages = qsa('.announcement-bar__message', bar);
    if (messages.length < 2) return;
    let index = 0;
    const go = (i) => { index = (i + messages.length) % messages.length; track.style.transform = `translateX(-${index * 100}%)`; };
    qs('[data-nav-prev]', bar)?.addEventListener('click', () => go(index - 1));
    qs('[data-nav-next]', bar)?.addEventListener('click', () => go(index + 1));
    if (bar.dataset.autoplay === 'true') {
      const speed = Number(bar.dataset.speed || 5) * 1000;
      setInterval(() => go(index + 1), speed);
    }
  }

  /* ---------------- Collection filters ---------------- */
  function initCollectionFilters() {
    const sidebar = qs('[data-filter-sidebar]');
    const toggle = qs('[data-filter-toggle]');
    if (sidebar && toggle) {
      const close = () => { sidebar.removeAttribute('open'); hideBackdrop(); activeCloser = null; };
      toggle.addEventListener('click', () => { sidebar.setAttribute('open', ''); showBackdrop(close); activeCloser = close; });
      qs('[data-filter-close]', sidebar)?.addEventListener('click', close);
    }
    qsa('[data-facet-input]').forEach((input) => input.addEventListener('change', () => input.closest('form').submit()));
    qsa('[data-facet-group-toggle]').forEach((btn) => btn.addEventListener('click', () => btn.closest('.facets__group').classList.toggle('is-collapsed')));
    qs('[data-sort-select]')?.addEventListener('change', (e) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      window.location.href = url.toString();
    });
  }

  /* ---------------- Sticky add to cart ---------------- */
  function initStickyAddToCart() {
    const bar = qs('[data-sticky-atc]');
    const anchor = qs('[data-atc-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([entry]) => bar.setAttribute('data-visible', String(!entry.isIntersecting)), { rootMargin: '-120px 0px 0px 0px' });
    io.observe(anchor);
  }

  /* ---------------- Scroll animations ---------------- */
  function initAnimations() {
    const items = qsa('[data-animate]');
    if (!items.length) return;
    if (document.documentElement.dataset.animations === 'false' || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); } });
    }, { threshold: 0.15 });
    items.forEach((el) => io.observe(el));
  }

  /* ---------------- Recently viewed ---------------- */
  const RECENTLY_VIEWED_KEY = 'forme:recently-viewed';
  function trackRecentlyViewed() {
    const data = qs('[data-track-product]');
    if (!data) return;
    const product = JSON.parse(data.textContent);
    let list = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
    list = list.filter((p) => p.id !== product.id);
    list.unshift(product);
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(list.slice(0, 8)));
  }
  function renderRecentlyViewed() {
    const container = qs('[data-recently-viewed]');
    if (!container) return;
    const currentId = Number(container.dataset.excludeId || 0);
    const list = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]').filter((p) => p.id !== currentId).slice(0, 4);
    if (!list.length) { container.closest('.section')?.setAttribute('hidden', ''); return; }
    container.innerHTML = list.map((p) => `
      <a class="card-product" href="${p.url}">
        <div class="card-product__media-wrap card-product__media-wrap--portrait">
          <img class="card-product__image card-product__image--front" src="${p.image}" alt="${p.title}" loading="lazy" width="400" height="500">
        </div>
        <p class="card-product__title">${p.title}</p>
        <div class="price"><span>${p.price}</span></div>
      </a>`).join('');
  }

  /* ---------------- Money formatting ---------------- */
  function formatMoney(cents) {
    const format = window.themeMoneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', amount).replace('{{ amount }}', amount);
  }

  /* ---------------- Newsletter ajax (progressive enhancement) ---------------- */
  function initNewsletterForms() {
    qsa('.newsletter__form').forEach((form) => {
      form.addEventListener('submit', () => { form.closest('.newsletter')?.classList.add('is-submitting'); });
    });
  }

  /* ---------------- boot ---------------- */
  document.addEventListener('DOMContentLoaded', () => {
    window.forme = window.forme || {};
    window.forme.formatMoney = formatMoney;

    qsa('[data-site-header]').forEach((el) => new SiteHeader(el));
    window.forme.cartDrawer = new CartDrawer();
    window.forme.quickAdd = new QuickAddModal();
    window.forme.searchDrawer = new SearchDrawer();

    qsa('[data-product-root]').forEach((root) => new ProductForm(root));

    initAccordions();
    initAnnouncementBar();
    initCollectionFilters();
    initStickyAddToCart();
    initAnimations();
    initNewsletterForms();
    trackRecentlyViewed();
    renderRecentlyViewed();

    qsa('[data-cart-toggle]').forEach((t) => t.addEventListener('click', () => window.forme.cartDrawer.refresh()));
  });

  window.forme = window.forme || {};
  window.forme.addToCart = addToCart;
})();
